import { useCallback } from 'react'
import { useTranslation } from 'react-i18next'

import { useAgentCatalogue, useAgentLabel } from '@/hooks/useAgentCatalogue'

/**
 * The name to show for an agent whose message arrived from ANOTHER session.
 *
 * ONE resolution for every hand-off surface. Both cards that render such a
 * message — the human hand-off summary and the agent-to-agent delegation — used
 * to answer this question their own way, and the second one inherited a
 * per-slug i18n map with four entries in it. `delegates_to` is config, so an
 * operator can point an edge at any loaded agent, and every agent outside those
 * four rendered as "Specialist" while the same agent showed its real name
 * everywhere else in the app.
 *
 * The chain is the same one the launcher and the agent strip use: the config
 * catalogue supplies the agent's own name, `useAgentLabel` localises it, and a
 * generic label covers a slug the catalogue does not carry (a retired agent
 * still named by an old message).
 */
export function useHandoffAgentLabel(): (slug: string) => string {
  const { t } = useTranslation()
  const agentLabel = useAgentLabel()
  const catalogue = useAgentCatalogue()

  return useCallback(
    (slug: string) => {
      const configName = catalogue.find((a) => a.slug === slug)?.name
      if (!configName) return t('chat.handoff.genericAgent')
      return agentLabel(slug, configName)
    },
    [t, agentLabel, catalogue],
  )
}
